import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import MobileFooter from './components/layout/MobileFooter';
import MobileHeader from './components/layout/MobileHeader';
import './App.css';
import './styles/main.css';
import { ThemeProvider } from './context/ThemeContext.jsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'flatpickr/dist/flatpickr.min.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-toastify/dist/ReactToastify.css';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import { ToastContainer } from 'react-toastify';
import LoadingSpinner from './components/ui/LoadingSpinner.jsx';
import { AuthProvider, PERMISSIONS } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy-loaded: each page (and whatever heavy libraries it pulls in — jsPDF,
// html2canvas, xlsx, the calendar, etc.) now ships in its own chunk, fetched
// only when that route is actually visited, instead of every page's code
// loading upfront regardless of which page is opened first.
const DashboardPage = lazy(() => import('./pages/DashboardPage.jsx'));
const VoituresPage = lazy(() => import('./pages/VoituresPage'));
const ReservationsPage = lazy(() => import('./pages/ReservationsPage'));
const ContractsPage = lazy(() => import('./pages/ContractsPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const EntretiensPage = lazy(() => import('./pages/EntretiensPage.jsx'));
const PlanningPage = lazy(() => import('./pages/PlanningPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const FinancialAnalyticsPage = lazy(() => import('./pages/FinancialAnalyticsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminEditCarsPage = lazy(() => import('./pages/AdminEditCarsPage'));
const AjouterClientPage = lazy(() => import('./pages/AjouterClientPage'));
const FormulaireVoiturePage = lazy(() => import('./pages/FormulaireVoiturePage'));
const ReservationEditPage = lazy(() => import('./pages/ReservationEditPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage.jsx'));
const FeedbackAdminPage = lazy(() => import('./pages/FeedbackAdminPage.jsx'));
const AccountsPage = lazy(() => import('./pages/AccountManagementPage'));
const PageNotFoundPage = lazy(() => import('./pages/PageNotFoundPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));

// Component to apply theme settings
const ThemeApplier = ({ children }) => {
  const { settings } = useSettings();
  
  useEffect(() => {
    // Apply sidebar color dynamically
    document.documentElement.style.setProperty('--sidebar-bg', settings.sidebarColor || '#1e293b');
    document.documentElement.style.setProperty('--sidebar-bg-lighter', '#334155');
    document.documentElement.style.setProperty('--sidebar-bg-darker', '#0f172a');
    document.documentElement.style.setProperty('--sidebar-border-color', 'rgba(255,255,255,0.1)');
  }, [settings.sidebarColor]);
  
  return children;
};

// Component to handle page titles
const PageWrapper = ({ children, title, sidebarOpen, onMenuToggle }) => {
  return (
    <>
      <Header title={title} sidebarOpen={sidebarOpen} onMenuToggle={onMenuToggle} />
      <div className="page-content-wrapper">
        <div className="page-container">
          {children}
        </div>
      </div>
    </>
  );
};

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isSignUpPage = location.pathname === '/signup';
  const isForgotPasswordPage = location.pathname === '/forgot-password';
  const isResetPasswordPage = location.pathname === '/reset-password';

  const toggleSidebar = () => setSidebarOpen(s => !s);

  // Close sidebar when screen grows to desktop size
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 992) setSidebarOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <AuthProvider>
      <SettingsProvider>
        <ThemeProvider>
          <ThemeApplier>
            <div className="App">
              {isLoginPage ? (
                <LoginPage />
              ) : isSignUpPage ? (
                <SignUpPage />
              ) : isForgotPasswordPage ? (
                <ForgotPasswordPage />
              ) : isResetPasswordPage ? (
                <ResetPasswordPage />
              ) : (
                <div className="app-container">
                  {/* Sidebar */}
                  <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

                  {/* Mobile top bar — hamburger + title + dark mode toggle */}
                  <MobileHeader onMenuToggle={toggleSidebar} />

                  {/* Main content wrapper */}
                  <div className={`main-content-wrapper ${sidebarOpen ? 'sidebar-open' : ''}`}>
                    <Suspense fallback={<LoadingSpinner fullPage />}>
                    <Routes>
                      {/* Protected routes with specific permissions */}
                      
                      {/* Dashboard - Requires view permissions for overview */}
                      <Route path="/" element={
                        <ProtectedRoute requiredPermission={[
                          PERMISSIONS.VIEW_CARS, 
                          PERMISSIONS.VIEW_RESERVATIONS,
                          PERMISSIONS.VIEW_CLIENTS,
                          PERMISSIONS.VIEW_FINANCIALS
                        ]}>
                          <PageWrapper title="pageDashboard" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <DashboardPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/dashboard" element={
    <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_DASHBOARD}>
                          <PageWrapper title="pageDashboard" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <DashboardPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      {/* Car Management */}
                      <Route path="/voitures" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_CARS}>
                          <PageWrapper title="pageCars" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <VoituresPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/admin-edit-cars/:id" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.EDIT_CARS}>
                          <PageWrapper title="pageEditCar" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <AdminEditCarsPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/formulaire-voiture" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.CREATE_CARS}>
                          <PageWrapper title="pageAddCar" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <FormulaireVoiturePage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      {/* Reservation Management */}
                      <Route path="/reservations" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_RESERVATIONS}>
                          <PageWrapper title="pageReservations" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <ReservationsPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/edit-reservation/:id" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.EDIT_RESERVATIONS}>
                          <PageWrapper title="pageEditReservation" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <ReservationEditPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/contracts" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_RESERVATIONS}>
                          <PageWrapper title="pageContracts" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <ContractsPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/planning" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_RESERVATIONS}>
                          <PageWrapper title="pagePlanning" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <PlanningPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      {/* Client Management */}
                      <Route path="/clients" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_CLIENTS}>
                          <PageWrapper title="pageClients" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <ClientsPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/ajouter-client" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.CREATE_CLIENTS}>
                          <PageWrapper title="pageAddClient" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <AjouterClientPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/ajouter-client/:id" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.EDIT_CLIENTS}>
                          <PageWrapper title="pageEditClient" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <AjouterClientPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      {/* Maintenance Management */}
                      <Route path="/entretiens" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_MAINTENANCE}>
                          <PageWrapper title="pageMaintenance" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <EntretiensPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      {/* Reports */}
                      <Route path="/reports" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_REPORTS}>
                          <PageWrapper title="pageReports" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <ReportsPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      {/* Financial Analytics (full breakdown — top expenses,
                          client profitability, vehicle ROI, monthly trend,
                          cost anomalies, PDF export) */}
                      <Route path="/financial-reports" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_FINANCIALS}>
                          <PageWrapper title="pageFinancialReports" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <FinancialAnalyticsPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      {/* Settings */}
                      <Route path="/settings" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_SETTINGS}>
                          <PageWrapper title="pageSettings" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <SettingsPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      {/* User & Account Management */}
                      <Route path="/users" element={
                        <ProtectedRoute requiredPermission={[PERMISSIONS.MANAGE_USERS, PERMISSIONS.MANAGE_ACCOUNT_USERS]}>
                          <PageWrapper title="pageUsers" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <UserManagementPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/accounts" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_ACCOUNTS}>
                          <PageWrapper title="pageAccounts" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <AccountsPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />
                      <Route path="/feedback" element={
                        <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_ACCOUNTS}>
                          <PageWrapper title="pageFeedback" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <FeedbackAdminPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      {/* Profile (accessible to all authenticated users) */}
                     <Route path="/profile" element={
                       <ProtectedRoute>
                         <PageWrapper title="pageProfile" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                           <ProfilePage />
                         </PageWrapper>
                       </ProtectedRoute>
                     } />

                      <Route path="/support" element={
                        <ProtectedRoute>
                          <PageWrapper title="pageSupport" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <SupportPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                      <Route path="*" element={
                        <ProtectedRoute>
                          <PageWrapper title="pageNotFound" sidebarOpen={sidebarOpen} onMenuToggle={toggleSidebar}>
                            <PageNotFoundPage />
                          </PageWrapper>
                        </ProtectedRoute>
                      } />

                    </Routes>
                    </Suspense>

                    <ToastContainer
                      position="bottom-right"
                      autoClose={5000}
                      hideProgressBar={false}
                      newestOnTop={false}
                      closeOnClick
                      pauseOnHover
                      draggable
                      theme="colored"
                    />
                  </div>
                  {/* Mobile Footer - hidden on desktop via CSS */}
                  <MobileFooter />
                </div>
              )}
            </div>
          </ThemeApplier>
        </ThemeProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
